import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useGaConfigUser } from "./auth/hooks/useGaConfigUser";
import { type UserProfile } from "./auth/utils/user";
import { localeToLanguageCode } from "./utils/conversions/languageCode";
import { type SupportedCurrencyCode } from "./utils/currency";
import { type DistanceSystem } from "./utils/distanceSystem";
import { useNavigateToNewLanguage } from "./utils/hooks/useNavigateToNewLanguage";
import {
  type SupportedLanguageCode,
  setLanguageCookie,
} from "./utils/language";
import { authKeys, useMutateUserProfile, useUserProfile } from "./auth/queries";
import { useMutateConfigsApi } from "./api/useConfigsApi";
import { useUserSettings, type UserSettings } from "./queries/useUserSettings";

interface UserPreferences extends UserSettings {
  savePrefers12Hour: Dispatch<boolean>;
  saveCurrencyCode: Dispatch<SupportedCurrencyCode>;
  saveDistanceSystem: Dispatch<DistanceSystem>;
  saveLanguage: Dispatch<SupportedLanguageCode>;
  user?: UserProfile;
  setUserAndSyncPreferences: (user: UserProfile) => void;
  loadingSession: boolean;
  setLoadingSession: Dispatch<SetStateAction<boolean>>;
}

export const UserContext = createContext<UserPreferences | undefined>(
  undefined,
);

function UserProvider(props: PropsWithChildren<{}>) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { navigateToNewLanguage } = useNavigateToNewLanguage();

  // We need to keep track of the loading state of the session based on the session id, because
  // we want to show the loading screen until we get the user info if the user is logged in
  const [loadingSession, setLoadingSession] = useState(false);

  const { data: user, isLoading: isUserProfileLoading } = useUserProfile();
  useGaConfigUser(user);

  const userProfileMutation = useMutateUserProfile();
  const isUserProfileMutationPending = userProfileMutation.isPending;

  const { status, prefers12Hour, currencyCode, distanceSystem, clockType } =
    useUserSettings();

  const [_, setLanguage] = useState(localeToLanguageCode(intl.locale));

  const updateUserProfile = userProfileMutation.mutate;
  const mutateConfigsApi = useMutateConfigsApi();
  const updateConfigsApi = mutateConfigsApi.mutate;

  const syncUserPrefrencesFromSession = useCallback(
    (user: UserProfile) => {
      updateConfigsApi({
        clockType: user.is24h ? "clock24" : "clock12",
        currencyCode: user.profileCurrency,
        distanceUnit: user.isKilometers ? "km" : "mi",
      });
      // Language
      setLanguageCookie(user.profileLanguage);
      setLanguage((prevLanguage) => {
        if (user.profileLanguage !== prevLanguage) {
          navigateToNewLanguage(user.profileLanguage);
        }
        return user.profileLanguage;
      });
    },
    [navigateToNewLanguage, updateConfigsApi],
  );

  // Callbacks related to the user preferences
  const savePrefers12Hour = useCallback(
    (prefers12Hour: boolean) => {
      updateConfigsApi({
        clockType: prefers12Hour ? "clock12" : "clock24",
      });
      if (user) {
        updateUserProfile({
          is24h: !prefers12Hour,
        });
      }
    },
    [updateConfigsApi, updateUserProfile, user],
  );
  const saveCurrencyCode = useCallback(
    (currencyCode: SupportedCurrencyCode) => {
      updateConfigsApi({
        currencyCode,
      });
      if (user) {
        updateUserProfile({
          profileCurrency: currencyCode,
        });
      }
    },
    [updateConfigsApi, updateUserProfile, user],
  );
  const saveDistanceSystem = useCallback(
    (distanceSystem: DistanceSystem) => {
      updateConfigsApi({
        distanceUnit: distanceSystem === "metric" ? "km" : "mi",
      });
      if (user) {
        updateUserProfile({
          isKilometers: distanceSystem === "metric",
        });
      }
    },
    [updateConfigsApi, updateUserProfile, user],
  );
  const saveLanguage = useCallback(
    (language: SupportedLanguageCode) => {
      setLanguage(language);
      setLanguageCookie(language);
      if (user) {
        updateUserProfile({
          profileLanguage: language,
        });
      }
    },
    [updateUserProfile, user],
  );

  // Set user and sync preferences when the user is loaded from the session
  const setUserAndSyncPreferences = useCallback(
    (user: UserProfile) => {
      queryClient.setQueryData(authKeys.user(), user);
      syncUserPrefrencesFromSession(user);
    },
    [syncUserPrefrencesFromSession, queryClient],
  );

  const value = useMemo(
    () =>
      ({
        status,
        clockType,
        prefers12Hour,
        currencyCode,
        distanceSystem,
        savePrefers12Hour,
        saveCurrencyCode,
        saveDistanceSystem,
        saveLanguage,
        user,
        // We dont want to use the setter directly to clear it, because we want to make sure that the user is also cleared from local storage
        setUserAndSyncPreferences,
        loadingSession:
          isUserProfileLoading ||
          isUserProfileMutationPending ||
          loadingSession,
        setLoadingSession,
      }) satisfies UserPreferences,
    [
      status,
      prefers12Hour,
      clockType,
      currencyCode,
      distanceSystem,
      savePrefers12Hour,
      saveCurrencyCode,
      saveDistanceSystem,
      saveLanguage,
      user,
      setUserAndSyncPreferences,
      loadingSession,
      isUserProfileLoading,
      isUserProfileMutationPending,
    ],
  );

  return <UserContext value={value}>{props.children}</UserContext>;
}

export default UserProvider;
